import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Button from '../Button';
import { useToast } from '../../context/ToastContext';

interface TennisRecordImportStepProps {
  teamId: number;
  onBack: () => void;
  onContinue: () => void;
}

interface ProgressMessage {
  type: string;
  message: string;
  timestamp: Date;
}

const TennisRecordImportStep: React.FC<TennisRecordImportStepProps> = ({
  teamId,
  onBack,
  onContinue,
}) => {
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState<any>(null);
  const [skipped, setSkipped] = useState(false);
  const [progressMessages, setProgressMessages] = useState<ProgressMessage[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Auto-scroll progress messages to bottom in modal
  useEffect(() => {
    if (progressMessages.length > 0 && fetching) {
      setTimeout(() => {
        const progressContainer = document.getElementById('wizard-modal-progress-container');
        if (progressContainer) {
          progressContainer.scrollTop = progressContainer.scrollHeight;
        }
      }, 100);
    }
  }, [progressMessages, fetching]);

  const handleFetch = async () => {
    if (!url.trim()) {
      toast.error('Please enter a TennisRecord.com URL');
      return;
    }

    try {
      // First, save the URL
      try {
        await axios.put(`/teams/${teamId}/tennisrecord-url`, {
          tennisrecord_url: url.trim(),
        });
      } catch (urlError: any) {
        // Handle URL validation errors
        const errorMessage = urlError.response?.data?.detail || 'Invalid URL. Please check the URL format.';
        toast.error(errorMessage);
        setFetching(false);
        return;
      }

      // Close any existing EventSource
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      setFetching(true);
      setProgressMessages([]);

      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setFetching(false);
        return;
      }

      // Create EventSource connection to SSE endpoint with token in URL
      const sseUrl = `${axios.defaults.baseURL}/teams/${teamId}/fetch-intelligence-stream?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(sseUrl);

      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setProgressMessages(prev => [...prev, {
            type: data.type,
            message: data.message,
            timestamp: new Date()
          }]);

          // If complete or error, close connection
          if (data.type === 'complete' || data.type === 'error') {
            eventSource.close();
            eventSourceRef.current = null;
            setFetching(false);

            // Show success message
            if (data.type === 'complete') {
              // Log the complete data for debugging
              console.log('Competitive intelligence fetch complete:', data);
              
              toast.success(data.message || 'Competitive intelligence fetched successfully!');
              setFetchResult({
                message: data.message || 'Competitive intelligence fetched successfully!',
                players_updated: data.updated_players ?? 0,
                players_created: data.created_users ?? 0,
                opponent_teams_created: data.updated_opponents ?? 0,
                matches_created: data.created_matches ?? 0,
                matches_with_results: data.matches_with_results ?? 0,
                lineups_created: data.lineups_created ?? 0,
                lineups_updated: data.lineups_updated ?? 0,
                added_team_members: data.added_team_members ?? 0,
                gender_updated_count: data.gender_updated_count ?? 0,
              });
            } else {
              toast.error(data.message || 'Failed to fetch competitive intelligence');
            }
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        if (eventSource.readyState === EventSource.CLOSED) {
          eventSource.close();
          eventSourceRef.current = null;
          setFetching(false);

          // Only show error toast if we don't already have a complete/error message
          const lastMessage = progressMessages[progressMessages.length - 1];
          if (!lastMessage || (lastMessage.type !== 'complete' && lastMessage.type !== 'error')) {
            toast.error('Connection error while fetching intelligence. Check console for details.');
          }
        }
      };

    } catch (error: any) {
      console.error('Failed to start intelligence fetch:', error);
      setFetching(false);

      // Handle FastAPI validation errors (can be string or array of error objects)
      let errorMessage = 'Failed to fetch competitive intelligence';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }

      toast.error(errorMessage);
    }
  };

  const handleSkip = () => {
    setSkipped(true);
    onContinue();
  };

  const handleContinue = () => {
    onContinue();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Step 4: TennisRecord.com Competitive Intelligence (Optional)
      </h2>

      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">What is TennisRecord.com?</h3>
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
          TennisRecord.com is a competitive tennis tracking website that shows match results,
          player ratings, team statistics, and opponent information. This import will fetch
          valuable competitive intelligence for your team.
        </p>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Example URL format:</strong>{' '}
          https://www.tennisrecord.com/adult/teamprofile.aspx?teamname=YourTeam&year=2025
        </p>
      </div>

      {!fetchResult && !skipped && (
        <div className="space-y-4">
          <div>
            <label htmlFor="tennisrecord_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              TennisRecord.com Team URL
            </label>
            <input
              type="url"
              id="tennisrecord_url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.tennisrecord.com/adult/teamprofile.aspx?teamname=..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              disabled={fetching}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleFetch} disabled={fetching || !url.trim()}>
              {fetching ? 'Fetching...' : 'Fetch Competitive Intelligence'}
            </Button>
          </div>
        </div>
      )}

      {/* Progress Modal - Reusing the same structure from TeamManagementPage */}
      {fetching && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <svg className="animate-spin h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100" id="modal-title">
                    Fetching Competitive Intelligence
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Please wait while we fetch player ratings and match data from TennisRecord.com. This may take a few minutes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress Messages */}
              <div 
                id="wizard-modal-progress-container"
                className="mt-6 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md p-4 max-h-96 overflow-y-auto"
              >
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Progress</h4>
                <div className="space-y-2">
                  {progressMessages.map((msg, idx) => {
                    // Only show spinner for the last message if it's in-progress and not complete/error
                    const isLastMessage = idx === progressMessages.length - 1;
                    const isInProgressType = ['start', 'fetching_team', 'scraping_opponent', 'creating_records', 'creating_users', 'fetching_results', 'processing_results', 'results_complete', 'fetching_gender'].includes(msg.type);
                    const showSpinner = isLastMessage && isInProgressType && fetching;
                    
                    return (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        {/* Icon based on message type and position */}
                        {showSpinner && (
                          <svg className="animate-spin w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        {!showSpinner && msg.type !== 'error' && (
                          <svg className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {msg.type === 'error' && (
                          <svg className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <div className="flex-1">
                          <p className={`${msg.type === 'error' ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                            {msg.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {msg.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 sm:mt-6">
                <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                  Please do not close this window or navigate away during this process.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {fetchResult && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <h3 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-2">Fetch Successful!</h3>
          <div className="text-sm text-green-800 dark:text-green-200">
            {fetchResult.message && (
              <p className="mb-2 font-medium">{fetchResult.message}</p>
            )}
            <div className="space-y-1">
              {(fetchResult.players_updated > 0 || fetchResult.players_created > 0) && (
                <p>
                  {fetchResult.players_updated} players updated
                  {fetchResult.players_created > 0 && ` (${fetchResult.players_created} new users created)`}
                </p>
              )}
              {fetchResult.opponent_teams_created > 0 && (
                <p>{fetchResult.opponent_teams_created} opponent teams created/updated</p>
              )}
              {fetchResult.matches_created > 0 && (
                <p>{fetchResult.matches_created} historical matches imported</p>
              )}
              {fetchResult.matches_with_results > 0 && (
                <p>{fetchResult.matches_with_results} matches with results updated</p>
              )}
              {(fetchResult.lineups_created > 0 || fetchResult.lineups_updated > 0) && (
                <p>
                  {fetchResult.lineups_created} lineups created, {fetchResult.lineups_updated} lineups updated
                </p>
              )}
              {fetchResult.added_team_members > 0 && (
                <p>{fetchResult.added_team_members} team members added</p>
              )}
              {fetchResult.gender_updated_count > 0 && (
                <p>{fetchResult.gender_updated_count} player genders updated</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button variant="outline" onClick={handleSkip}>
            Skip for now
          </Button>
        </div>
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={!fetchResult && !skipped && !url}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default TennisRecordImportStep;

